import { useEffect, useState } from "react";
import { offstreetClient } from "../../../../api/client";
import type { RouterOutputs } from "../../../../api/types";
import { usePermitFormActions } from "../../PermitContextProvider";
import type { PaymentFormProps } from "./PaymentForm";
import { PaymentFormError } from "./types";

type UsePaymentSessionProps = Pick<PaymentFormProps, "tenantId" | "locale" | "setDuplicatedPermit">;

export const usePaymentSession = ({ tenantId, locale, setDuplicatedPermit }: UsePaymentSessionProps) => {
	const { getPermitPostData, setError, setIsProcessingPermit, setPaymentRetryError } = usePermitFormActions();
	const [paymentSession, setPaymentSession] =
		useState<RouterOutputs["paymentSession"]["createPaymentSessionPublic"]>();
	const [sessionRequest, setSessionRequest] = useState(0);
	const [isWaitingToRetry, setIsWaitingToRetry] = useState(false);
	const { mutate: createPaymentSessionPublic, isPending: isCreatingSession } =
		offstreetClient.paymentSession.createPaymentSessionPublic.useMutation();
	const { mutate: acknowledgeFailedPayment, isPending: isAcknowledgingPayment } =
		offstreetClient.paymentSession.acknowledgeFailedPayment.useMutation();

	// biome-ignore lint/correctness/useExhaustiveDependencies: Only request a payment session on mount and on restart
	useEffect(() => {
		let retryTimeout: ReturnType<typeof setTimeout> | undefined;
		const requestSession = (attempt: number) => {
			setIsWaitingToRetry(false);
			createPaymentSessionPublic(
				{
					...getPermitPostData(),
					tenantId,
					returnUrl: `${window.location.origin}/${locale}/payments/touchnet/callback?resume=${encodeURIComponent(window.location.pathname + window.location.search)}`,
				},
				{
					onSuccess: (data) => {
						if (data.session) {
							setIsProcessingPermit(false);
							setPaymentRetryError(null);
							setPaymentSession(data);
						} else if (data.permit.isPaymentPending && attempt < 3) {
							setIsWaitingToRetry(true);
							retryTimeout = setTimeout(() => requestSession(attempt + 1), 3_000);
						} else if (data.permit.isPaymentPending) {
							setIsProcessingPermit(false);
							setPaymentRetryError(PaymentFormError.UNCONFIRMED);
						} else {
							setIsProcessingPermit(false);
							setPaymentRetryError(null);
							setDuplicatedPermit({
								plate: data.permit.permit_vehicles_vehicle[0]?.vehicle.plate!,
								permitIntervals: data.permit.permitIntervals.map((interval) => ({
									startDateTimeISO: interval.startDateTime,
									endDateTimeISO: interval.endDateTime,
								})),
								publicIdentifier: data.permit.publicIdentifier,
							});
						}
					},
					onError: (error) => {
						setIsProcessingPermit(false);
						setError(error);
					},
				},
			);
		};
		setIsProcessingPermit(true);
		requestSession(0);
		return () => {
			clearTimeout(retryTimeout);
			setIsWaitingToRetry(false);
			setIsProcessingPermit(false);
		};
	}, [sessionRequest, tenantId]);

	const requestNewSession = () => setSessionRequest((count) => count + 1);

	const restart = () => {
		const paymentId = paymentSession?.session?.paymentId;
		setIsProcessingPermit(true);
		setError(undefined);
		setPaymentSession(undefined);
		if (paymentId) {
			acknowledgeFailedPayment({ paymentId }, { onSettled: requestNewSession });
			return;
		}
		requestNewSession();
	};

	return { paymentSession, isCreating: isAcknowledgingPayment || isCreatingSession || isWaitingToRetry, restart };
};
